import { useEffect, useState, useMemo, memo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { AppState } from "@Store/index";
import CustomLink from "@Components/CustomLink";
import urlJoin from "helpers/urljoin";
import useWhitelabel from "hooks/useWhitelabel";
import { convertToWhitelabelURL } from "@Pages/WhitelabelHome/Desktop/helpers/WhiteLabelRedirectionHelper";
import wrapperHOC from "@Utils/wrapperHOC";
import { useResponsiveness } from "hooks/useResponsiveness";
import styles from "./ListingSection.module.scss";

type ListingSectionProps = {
  title: string;
  fetchFn: (id: string, language?: string) => Promise<any[]>;
  type: "city" | "area" | "university";
  loadingText?: string;
  className?: string;
  ssrData?: any[];
};

const ListingSection = ({
  title,
  fetchFn,
  type,
  loadingText = "Loading...",
  className,
  ssrData,
}: ListingSectionProps) => {
  const { region, userLanguage } = useSelector(
    ({ searchPageData, appData }: AppState) => ({
      region: searchPageData.region,
      userLanguage: appData?.userLanguage,
    }),
    shallowEqual,
  );

  const [items, setItems] = useState<any[]>(ssrData && ssrData.length > 0 ? ssrData : []);
  const [isLoading, setIsLoading] = useState<boolean>(!(ssrData && ssrData.length > 0));
  const [selectedLetter, setSelectedLetter] = useState<string | null>(null);
  const { isWhitelabel, isDemoSubdomain, partner_uuid } = useWhitelabel();
  const { isAbove } = useResponsiveness();
  const isDesktop = isAbove("md");

  useEffect(() => {
    // Skip fetch if SSR data is already available
    if (ssrData && ssrData.length > 0) return;

    const fetchAllItems = async () => {
      if (!region?.id) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const allItems = await fetchFn(region.id, userLanguage);
        const filteredItems = allItems.filter(
          (item: any) => item.active_inventories_in_bound_count > 0,
        );
        setItems(filteredItems);
      } catch (error) {
        console.error(`Error fetching ${type}s:`, error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAllItems();
  }, [region?.id]);

  const itemsByLetter = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    items.forEach((item) => {
      const firstLetter = item.name.charAt(0).toUpperCase();
      if (!grouped[firstLetter]) {
        grouped[firstLetter] = [];
      }
      grouped[firstLetter].push(item);
    });
    return grouped;
  }, [items]);

  const availableLetters = useMemo(
    () => Object.keys(itemsByLetter).sort((a, b) => a.localeCompare(b)),
    [itemsByLetter],
  );

  const filteredItems = useMemo(() => {
    if (!selectedLetter) return itemsByLetter;
    return { [selectedLetter]: itemsByLetter[selectedLetter] };
  }, [selectedLetter, itemsByLetter]);

  const handleLetterClick = (letter: string) => {
    if (selectedLetter === letter) {
      setSelectedLetter(null);
    } else {
      setSelectedLetter(letter);
    }
  };

  const getUrl = (canonicalName: string) => {
    let url = urlJoin("/places/search", canonicalName);
    if (isWhitelabel) {
      url = convertToWhitelabelURL(`places/search/${canonicalName}`, isDemoSubdomain, partner_uuid);
    }
    return url;
  };

  if (!region?.id || items.length === 0) {
    if (isLoading) {
      return (
        <div className={`${styles.listingSection} ${className || ""}`}>
          <div className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
          </div>
          <div style={{ padding: "20px", textAlign: "center" }}>{loadingText}</div>
        </div>
      );
    }
    return null;
  }

  return (
    <>
      <div className={`${styles.listingSection} ${className || ""}`}>
        <div className={styles.header}>
          <h2 className={styles.title}>{title}</h2>
        </div>

        <div className={styles.alphabetFilter}>
          {availableLetters.map((letter) => (
            <button
              key={letter}
              type="button"
              className={`${styles.letterButton} ${selectedLetter === letter ? styles.active : ""}`}
              onClick={() => handleLetterClick(letter)}
            >
              {letter}
            </button>
          ))}
        </div>

        <div className={styles.grid}>
          {Object.entries(filteredItems)
            .sort(([letterA], [letterB]) => letterA.localeCompare(letterB))
            .map(([letter, letterItems], idx) => {
              const uniqueId = `toggle-${type}-${idx}`;
              const hasMore = letterItems.length > 5;

              return (
                <div key={letter} className={styles.letterGroup}>
                  <input type="checkbox" id={uniqueId} className={styles.toggleCheckbox} />
                  <h3 className={styles.letterHeading}>{letter}</h3>
                  <div className={styles.itemList}>
                    {letterItems.slice(0, 5).map((item: any) => (
                      <CustomLink
                        key={item.id}
                        href={getUrl(item.canonical_name)}
                        className={styles.itemLink}
                      >
                        {item.name}
                      </CustomLink>
                    ))}
                    {hasMore && (
                      <div className={styles.additionalItems}>
                        {letterItems.slice(5).map((item: any) => (
                          <CustomLink
                            key={item.id}
                            href={getUrl(item.canonical_name)}
                            className={styles.itemLink}
                          >
                            {item.name}
                          </CustomLink>
                        ))}
                      </div>
                    )}
                  </div>
                  {hasMore && (
                    <div className={styles.toggleLabelContainer}>
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor={uniqueId} className={styles.showMoreLabel}>
                        See All
                      </label>
                      {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                      <label htmlFor={uniqueId} className={styles.showLessLabel}>
                        See Less
                      </label>
                    </div>
                  )}
                </div>
              );
            })}
        </div>
      </div>
      {!isDesktop && <div className={styles.bottomBorder} />}
    </>
  );
};

export default wrapperHOC(memo(ListingSection), {
  componentName: "ListingSection-SearchPage",
  showForChina: true,
});
